enum BrowserEngine {
  Firefox,
  V8,
  Webkit,
  Unknown
}

export class EngineError extends Error {
  code: string
  messageWithoutCode: string
  backtrace?: string
  context: Record<string, string>
  tags: Record<string, string>

  constructor(code: string, message: string) {
    super(message)
    this.messageWithoutCode = message
    this.message = `${code}: ${message}`
    this.code = code
    this.context = {
      stack: this.stack ?? "<unknown>",
    }
    this.tags = {}
  }

  setContext(key: string, value: string) {
    this.context[key] = value
  }

  setBacktrace(backtrace: string) {
    this.context.backtrace = backtrace
    this.backtrace = backtrace
  }

  setTag(key: string, value: string) {
    this.tags[key] = value
  }

  // Custom stacktrace for sentry
  // We want to be able to provide sentry with both the stack at the point
  // at which the engine error was created, and the stack at which we captured it
  // with preference for the engine stack (in the EngineError case)
  get stacktrace() {
    if (!this.backtrace) {
      return this.stack
    }

    // We need different behaviour here depending on if we are gecko or v8
    let superStack = this.stack ?? ""

    // First, make sure that all stacks end with a newline

    if (!this.backtrace.endsWith("\n")) {
      this.backtrace += "\n"
    }

    if (!superStack.endsWith("\n")) {
      superStack += "\n"
    }

    // Try and determine which browser we are using.
    // Different browsers have different stack trace formats, and we would like
    // to adhere to the format of the browser being used, otherwise sentry
    // will struggle to parse the stacktrace, capturing it incorrectly.

    const engine = determineBrowserEngine(superStack)

    // Create a fake stack frame, representing the boundary between the UI side api stack
    // and the stack from the workers POV.

    let boundary

    switch (engine) {
      case BrowserEngine.Firefox: {
        boundary = `wasm_worker_boundary@${window.location.origin}/wasm/worker/boundary`
        break
      }
      case BrowserEngine.V8: {
        boundary = `    at wasm_worker_boundary (${window.location.origin}/wasm/worker/boundary:0)`
        break
      }
      case BrowserEngine.Webkit: {
        boundary = `wasm_worker_boundry@${window.location.origin}/wasm/worker/boundry`
        break
      }
      case BrowserEngine.Unknown:
      default:
        // If we don't know what browser engine this is then just return the normal stack
        // The wasm worker stack will be attached as context to the error.
        return superStack
    }

    boundary += "\n"
    const backtrace = `${this.backtrace}${boundary}${superStack}`
    return backtrace
  }
}

function determineBrowserEngine(stack: string): BrowserEngine {
  // V8 formats its errors like

  // Error
  //   at baz (filename.js:10:15)
  //   at bar (filename.js:6:3)
  //   at foo (filename.js:2:3)
  //   at filename.js:13:1

  const errorName = Error.name
  const atMatches = (stack.match(/ at /g) || []).length

  if (stack.startsWith(errorName) && atMatches > 0) {
    return BrowserEngine.V8
  }

  // Safari is the only browser that uses '@[wasm code]' and ' line '

  /*
    @https://alpha.kscope.com/assets/engine_wasm-kmUfQ0NW.js:2:61922 (index-CtcXcqc-.js, line 51)
    <?>.wasm-function[871]@[wasm code]
    <?>.wasm-function[1122]@[wasm code]
    <?>.wasm-function[2505]@[wasm code]
    <?>.wasm-function[8250]@[wasm code]
    <?>.wasm-function[8651]@[wasm code]
    te@https://alpha.kscope.com/assets/engine_wasm-kmUfQ0NW.js:2:1271
    a@https://alpha.kscope.com/assets/engine_wasm-kmUfQ0NW.js:1:1677

    S2@https://alpha.kscope.com/assets/index-CtcXcqc-.js:90:5119 (index-CtcXcqc-.js, line 51)
    @https://alpha.kscope.com/assets/index-CtcXcqc-.js:35:62015
    <?>.wasm-function[8297]@[wasm code]
    <?>.wasm-function[425]@[wasm code]
    <?>.wasm-function[792]@[wasm code]
    <?>.wasm-function[2505]@[wasm code]
    <?>.wasm-function[8250]@[wasm code]
    <?>.wasm-function[8651]@[wasm code]
    Ule@https://alpha.kscope.com/assets/index-CtcXcqc-.js:35:1291
    a@https://alpha.kscope.com/assets/index-CtcXcqc-.js:34:10247
  */

  const hasLine = (stack.match(/ line /g) || []).length > 0
  const hasWasmCode = (stack.match(/@\[wasm code\]/g) || []).length > 0

  if (hasLine || hasWasmCode) {
    return BrowserEngine.Webkit
  }

  // Firefox formats its errors as function@file:x:?y

  /*
  S2@https://alpha.kscope.com/assets/index-CtcXcqc-.js:90:5114
  ez/e.wbg.__wbg_new_004b538a21c0231c@https://alpha.kscope.com/assets/index-CtcXcqc-.js:35:62000
  @https://alpha.kscope.com/assets/engine_wasm_bg-DorlvKEZ.wasm:wasm-function[8297]:0x5e867f
  @https://alpha.kscope.com/assets/engine_wasm_bg-DorlvKEZ.wasm:wasm-function[425]:0x15efb6
  @https://alpha.kscope.com/assets/engine_wasm_bg-DorlvKEZ.wasm:wasm-function[792]:0x215212
  @https://alpha.kscope.com/assets/engine_wasm_bg-DorlvKEZ.wasm:wasm-function[2505]:0x45ed40
  @https://alpha.kscope.com/assets/engine_wasm_bg-DorlvKEZ.wasm:wasm-function[8250]:0x5e7dcb
  @https://alpha.kscope.com/assets/engine_wasm_bg-DorlvKEZ.wasm:wasm-function[8651]:0x5f303f
  Ule@https://alpha.kscope.com/assets/index-CtcXcqc-.js:35:1265
  a@https://alpha.kscope.com/assets/index-CtcXcqc-.js:34:10246
  VoidFunction*ez/e.wbg.__wbg_queueMicrotask_97d92b4fcc8a61c5@https://alpha.kscope.com/assets/index-CtcXcqc-.js:35:65128
  @https://alpha.kscope.com/assets/engine_wasm_bg-DorlvKEZ.wasm:wasm-function[4485]:0x56688a
  @https://alpha.kscope.com/assets/engine_wasm_bg-DorlvKEZ.wasm:wasm-function[6448]:0x5ba039
  @https://alpha.kscope.com/assets/engine_wasm_bg-DorlvKEZ.wasm:wasm-function[3143]:0x4e8cbf
  @https://alpha.kscope.com/assets/engine_wasm_bg-DorlvKEZ.wasm:wasm-function[8284]:0x5e84e6
  Dle@https://alpha.kscope.com/assets/index-CtcXcqc-.js:35:763
  a@https://alpha.kscope.com/assets/index-CtcXcqc-.js:34:10246
  n@https://alpha.kscope.com/assets/index-CtcXcqc-.js:51:8920
  */

  const atSymbolMatches = (stack.match(/@.*:/) || []).length

  if (atSymbolMatches > 0) {
    return BrowserEngine.Firefox
  }

  return BrowserEngine.Unknown
}
